import React from "react";
import {
    getCellProperty,
    isAlphaNumericKey,
    isNavigationKey,
    keyCodes,
    type Cell,
    type CellTemplate,
    type Compatible,
    type Uncertain,
    type UncertainCompatible,
} from "@silevis/reactgrid";
import {
    ChevronRight,
    ExpandMore,
    Delete,
    ContentCopy,
    LocalAtmRounded,
    MoreVert,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import type { Id } from "@silevis/reactgrid";

/**
 * Actions that can be dispatched from the chevron cell.
 * Using a discriminated union instead of magic strings through onCellChanged.
 */
export type ChevronRowAction =
    | { type: "DELETE_ROW"; rowId: Id }
    | { type: "DUPLICATE_ROW"; rowId: Id }
    | { type: "ADD_ADDON"; rowId: Id }
    | { type: "EDIT_GROUP_SETTINGS"; rowId: Id };

export interface ChevronGridCell extends Cell {
    type: "chevron";
    text: string;
    isExpanded: boolean;
    hasChildren: boolean;
    parentId?: Id;
    indentLevel?: number;
    isDeletable?: boolean;
    canAddAddon?: boolean;
    hasSettings?: boolean;
    /** Injected by the view mapper so action callbacks can identify the source row. */
    rowId?: Id;
}

/**
 * Custom ChevronCellTemplate that follows ReactGrid's cell template protocol EXACTLY.
 *
 * The interaction model matches the built-in ChevronCellTemplate:
 * - Double-click or Enter → opens edit mode with cursor at end of text
 * - Typing a character → replaces text with that character and opens edit mode
 * - Delete/Backspace → clears text and opens edit mode
 * - Space → toggles expand/collapse (if has children)
 * - Escape in edit mode → reverts changes
 * - Enter in edit mode → commits changes (handled by ReactGrid, NOT by us)
 * - Clicking away → commits changes via onBlur
 *
 * Additionally renders action buttons (delete, duplicate, addon, settings)
 * via constructor-injected callback.
 */
export class ChevronCellTemplate implements CellTemplate<ChevronGridCell> {
    private wasEscKeyPressed = false;

    constructor(private onRowAction?: (action: ChevronRowAction) => void) {}

    getCompatibleCell(
        uncertainCell: Uncertain<ChevronGridCell>,
    ): Compatible<ChevronGridCell> {
        const text = getCellProperty(uncertainCell, "text", "string");
        const isExpanded = getCellProperty(
            uncertainCell,
            "isExpanded",
            "boolean",
        );
        const hasChildren = getCellProperty(
            uncertainCell,
            "hasChildren",
            "boolean",
        );
        const indentLevel = getCellProperty(
            uncertainCell,
            "indentLevel",
            "number",
        );

        return {
            type: "chevron",
            text: text || "",
            isExpanded: isExpanded || false,
            hasChildren: hasChildren || false,
            indentLevel: indentLevel || 0,
            isDeletable: uncertainCell.isDeletable || false,
            canAddAddon: uncertainCell.canAddAddon || false,
            hasSettings: uncertainCell.hasSettings || false,
            rowId: uncertainCell.rowId,
            parentId: uncertainCell.parentId,
            style: uncertainCell.style,
            nonEditable: uncertainCell.nonEditable || false,
            value: text || "",
        };
    }

    /**
     * Follows the EXACT same protocol as ReactGrid's built-in ChevronCellTemplate.
     *
     * Key insight: ReactGrid calls this with keyCode=1 (POINTER) and key="DoubleClick"
     * for double-clicks. We must return enableEditMode: true for that case.
     */
    handleKeyDown(
        cell: Compatible<ChevronGridCell>,
        keyCode: number,
        ctrl: boolean,
        shift: boolean,
        alt: boolean,
        key?: string,
        capsLock?: boolean,
    ): { cell: Compatible<ChevronGridCell>; enableEditMode: boolean } {
        // F-keys (except F2) → don't enter edit mode
        // keyCodes.F2 = 113
        if (keyCode >= 112 && keyCode <= 123 && keyCode !== 113) {
            return { cell, enableEditMode: false };
        }
        if (keyCode === 113) {
            return { cell, enableEditMode: true };
        }

        // POINTER (1) = double-click, ENTER (13) = Enter key
        let enableEditMode =
            keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER;
        const newCell = { ...cell };

        // Space → toggle expand/collapse if has children (and not shift+space)
        if (keyCode === keyCodes.SPACE && cell.hasChildren && !shift) {
            newCell.isExpanded = !newCell.isExpanded;
        }
        // Alphanumeric key → replace text with typed character, enter edit mode
        else if (
            !ctrl &&
            !alt &&
            isAlphaNumericKey(keyCode) &&
            !(shift && keyCode === keyCodes.SPACE)
        ) {
            // Set text to the typed character (ReactGrid's protocol: type-to-replace)
            newCell.text = key && key.length === 1 ? key : "";
            enableEditMode = true;
        }

        return { cell: newCell, enableEditMode };
    }

    /**
     * Called when ReactGrid merges cell changes. Only update text and isExpanded.
     */
    update(
        cell: Compatible<ChevronGridCell>,
        cellToMerge: UncertainCompatible<ChevronGridCell>,
    ): Compatible<ChevronGridCell> {
        return this.getCompatibleCell({
            ...cell,
            isExpanded: cellToMerge.isExpanded,
            text: cellToMerge.text,
        });
    }

    render(
        cell: Compatible<ChevronGridCell>,
        isInEditMode: boolean,
        onCellChanged: (
            cell: Compatible<ChevronGridCell>,
            commit: boolean,
        ) => void,
    ): React.ReactNode {
        const indent = (cell.indentLevel || 0) * 24;

        const dispatchRowAction =
            (action: ChevronRowAction) => (e: React.MouseEvent) => {
                e.stopPropagation();
                if (this.onRowAction) {
                    this.onRowAction(action);
                }
            };

        const rowId = cell.rowId ?? "";

        // ── Edit Mode ──────────────────────────────────────────────────────
        if (isInEditMode) {
            return (
                <input
                    className="rg-input"
                    ref={(input) => {
                        if (input) {
                            input.focus();
                            // Place cursor at the end (same as built-in)
                            input.setSelectionRange(
                                input.value.length,
                                input.value.length,
                            );
                        }
                    }}
                    defaultValue={cell.text}
                    onChange={(e) => {
                        // Update cell state continuously (commit=false → stay in edit mode)
                        onCellChanged(
                            this.getCompatibleCell({
                                ...cell,
                                text: e.currentTarget.value,
                            }),
                            false,
                        );
                    }}
                    onBlur={(e) => {
                        // Commit on blur, unless Escape was pressed
                        onCellChanged(
                            this.getCompatibleCell({
                                ...cell,
                                text: e.currentTarget.value,
                            }),
                            !this.wasEscKeyPressed,
                        );
                        this.wasEscKeyPressed = false;
                    }}
                    onKeyDown={(e) => {
                        // Stop propagation for keys we own (typing, navigation)
                        // This prevents ReactGrid from handling them while we're editing
                        if (
                            isAlphaNumericKey(e.keyCode) ||
                            isNavigationKey(e.keyCode)
                        ) {
                            e.stopPropagation();
                        }
                        // Escape → flag so onBlur skips commit
                        if (e.keyCode === keyCodes.ESCAPE) {
                            this.wasEscKeyPressed = true;
                        }
                        // DO NOT handle Enter here — ReactGrid handles it externally
                        // to properly exit edit mode and fire onCellsChanged
                    }}
                    onCopy={(e) => e.stopPropagation()}
                    onCut={(e) => e.stopPropagation()}
                    onPaste={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        font: "inherit",
                        color: "inherit",
                        minWidth: 0,
                        paddingLeft: `${indent}px`,
                    }}
                />
            );
        }

        // ── View Mode ──────────────────────────────────────────────────────
        return (
            <Box
                className="rg-chevron-cell"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                    paddingLeft: `${indent}px`,
                    "& .action-buttons": {
                        visibility: "hidden",
                    },
                    "&:hover .action-buttons": {
                        visibility: "visible",
                    },
                }}
            >
                {/* Action icons — left side, before name, no background */}
                <Box
                    className="action-buttons"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: 32,
                        flexShrink: 0,
                    }}
                >
                    {cell.isDeletable && (
                        <Delete
                            sx={{
                                fontSize: 14,
                                cursor: "pointer",
                                color: "grey",
                            }}
                            onClick={dispatchRowAction({
                                type: "DELETE_ROW",
                                rowId,
                            })}
                        />
                    )}
                    {cell.isDeletable && (
                        <ContentCopy
                            sx={{
                                fontSize: 14,
                                cursor: "pointer",
                                color: "grey",
                                pr: 0.5,
                            }}
                            onClick={dispatchRowAction({
                                type: "DUPLICATE_ROW",
                                rowId,
                            })}
                        />
                    )}
                </Box>

                {/* Cell text */}
                <span
                    style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        paddingRight: 8,
                    }}
                >
                    {cell.text}
                </span>

                {/* Settings menu icon — after name, inline */}
                {cell.hasSettings && !cell.nonEditable && (
                    <MoreVert
                        className="action-buttons"
                        sx={{ fontSize: 14, cursor: "pointer", color: "grey" }}
                        onClick={dispatchRowAction({
                            type: "EDIT_GROUP_SETTINGS",
                            rowId,
                        })}
                    />
                )}

                {/* Addon tag icon for addon rows */}
                {cell.canAddAddon && String(rowId).startsWith("addon") && (
                    <LocalAtmRounded
                        sx={{ fontSize: 14, cursor: "pointer", color: "grey" }}
                        onClick={dispatchRowAction({
                            type: "ADD_ADDON",
                            rowId,
                        })}
                    />
                )}

                {/* Spacer to push chevron to the right */}
                <div style={{ flex: 1 }} />

                {/* Expand/collapse chevron — right side */}
                {cell.hasChildren ? (
                    <div
                        className="chevron"
                        onPointerDown={(e) => {
                            e.stopPropagation();
                            onCellChanged(
                                this.getCompatibleCell({
                                    ...cell,
                                    isExpanded: !cell.isExpanded,
                                }),
                                true,
                            );
                        }}
                        style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            padding: "2px 8px 2px 2px",
                        }}
                    >
                        {cell.isExpanded ? (
                            <ExpandMore sx={{ fontSize: 16 }} />
                        ) : (
                            <ChevronRight sx={{ fontSize: 16 }} />
                        )}
                    </div>
                ) : null}
            </Box>
        );
    }
}
